import React, {useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';
import {InputText} from 'primereact/inputtext';
import {InputTextarea} from 'primereact/inputtextarea';
import {MultiSelect} from 'primereact/multiselect';
import {RadioButton} from 'primereact/radiobutton';
import {Tag} from 'primereact/tag';
import {MapContainer, TileLayer, Marker, Popup, useMap} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import PageLayout from '../PageLayout';
import Btn from '../../ui/Btn';
import appStore from '../../storage/AppStore';
import systemStore from '../../storage/SystemStore';
import {post} from '../../common/requester';

const STATUSES = [
  {label: 'Все статусы', value: ''},
  {label: 'Новые', value: 'new'},
  {label: 'На проверке', value: 'review'},
  {label: 'Приняты', value: 'approved'},
  {label: 'Отклонены', value: 'rejected'},
  {label: 'Дубликаты', value: 'duplicate'}
];
const STATUS_VIEW = {
  new: {label: 'Новый', severity: 'info'},
  review: {label: 'На проверке', severity: 'warning'},
  approved: {label: 'Принят', severity: 'success'},
  rejected: {label: 'Отклонён', severity: 'danger'},
  duplicate: {label: 'Дубликат', severity: 'secondary'}
};
const PER_PAGE = 30;
const SPORT_HINTS = [
  {id: 'JIU_JITSU', pattern: /джиу[\s-]*джитсу|jiu[\s-]*jitsu|bjj/i},
  {id: 'ICE_HOCKEY', pattern: /хокке|hockey/i},
  {id: 'JUDO', pattern: /дзюдо|judo/i},
  {id: 'CHESS', pattern: /шахмат|chess/i},
  {id: 'BOXING', pattern: /бокс|boxing/i},
  {id: 'FUTSAL', pattern: /футзал|мини[\s-]*футбол|futsal/i},
  {id: 'TABLE_TENNIS', pattern: /настольн[а-яё]*\s+теннис|table[\s-]*tennis|пинг[\s-]*понг/i},
  {id: 'TENNIS', pattern: /теннис|tennis/i},
  {id: 'BASKETBALL', pattern: /баскетбол|баскетбольн|basketball/i},
  {id: 'DANCE', pattern: /танц|танцев|хореограф|dance|dancing/i},
  {id: 'VOLLEYBALL', pattern: /волейбол|волейбольн|volleyball/i},
  {id: 'WRESTLING', pattern: /борьб|борцов|wrestling/i},
  {id: 'MMA', pattern: /(?:^|[^a-z])mma(?:$|[^a-z])|мма|смешанн[а-яё]*\s+единоборств/i},
  {id: 'SKIING', pattern: /лыж|skiing/i},
  {id: 'TAEKWONDO', pattern: /тхэквонд|таэквонд|taekwondo/i},
  {id: 'SWIMMING', pattern: /плавани|пловц|бассейн|swimming/i},
  {id: 'ATHLETICS', pattern: /легк[а-яё]*\s+атлетик|athletics/i},
  {id: 'KARATE', pattern: /карат[эе]|karate/i},
  {id: 'FOOTBALL', pattern: /футбол|футбольн|football|soccer/i}
];
const nameForSport = (name, id) => {
  if (id === 'TENNIS') return name.replace(/настольн[^\s]*\s+теннис(?:а|у|ом)?|table[\s-]*tennis/gi, '');
  if (id === 'FOOTBALL') return name.replace(/мини[\s-]*футбол(?:а|у|ом)?/gi, '');
  return name;
};
const suggestSports = (item) => {
  const name = valueText(item?.name);
  const rubrics = (item?.data?.source_categories || []).join(' ');
  const searches = (item?.data?.searches || []).join(' ');
  return SPORT_HINTS.map(({id, pattern}) => {
    const source = pattern.test(nameForSport(name, id)) ? 'name' : pattern.test(nameForSport(rubrics, id)) ? 'rubrics' : pattern.test(nameForSport(searches, id)) ? 'searches' : null;
    return source ? {id, source} : null;
  }).filter(Boolean);
};
const MATCH_STOPWORDS = new Set([
  'клуб', 'клубы', 'школа', 'школы', 'академия', 'центр', 'комплекс', 'секция', 'секции', 'студия', 'студии', 'арена',
  'спортивный', 'спортивная', 'спортивное', 'спортивных', 'спортивно', 'детский', 'детская', 'детско',
  'юношеская', 'юношеский', 'олимпийского', 'резерва', 'сдюшор', 'дюсш', 'имени', 'при', 'для',
  'баскетбольный', 'баскетбольная', 'футбольный', 'футбольная', 'волейбольный', 'волейбольная',
  'танцевальный', 'хоккейный', 'хоккейная', 'шахматный', 'теннисный', 'единоборств', 'бишкек',
  'basketball', 'football', 'volleyball', 'sports', 'sport', 'club', 'school', 'academy', 'arena', 'center'
]);
const distinctiveTokens = (name) => [...new Set(valueText(name).toLowerCase().replace(/ё/g, 'е').replace(/[^\p{L}\p{N}]+/gu, ' ').trim().split(/\s+/).filter(x => x.length >= 3 && !MATCH_STOPWORDS.has(x) && !/^\d+$/.test(x)))];
const duplicateScore = (a, b) => {
  const first = distinctiveTokens(a), second = distinctiveTokens(b);
  if (!first.length || !second.length) return 0;
  const common = first.filter(x => second.includes(x)).length;
  return common ? 2 * common / (first.length + second.length) : 0;
};
const findCompanyMatches = async (item) => {
  const words = distinctiveTokens(item.name).sort((a, b) => b.length - a.length).slice(0, 3);
  if (!words.length) return [];
  const found = new Map();
  for (const word of words) {
    const result = await post('search_companies_dropdown', {name: word, country_id: item.country_id || 'KG', region_id: item.region_id || 'KG01'});
    for (const company of result.companies || []) found.set(company.id, company);
  }
  return [...found.values()].map(company => ({...company, score: duplicateScore(item.name, company.name)}))
    .filter(company => company.score >= 0.45).sort((a, b) => b.score - a.score).slice(0, 6);
};
const message = (key, fallback) => appStore.tr?.[key] || fallback;
const valueText = (value) => value === null || value === undefined ? '' : String(value);
const sourceTags = (values, limit = 0) => {
  const items = Array.isArray(values) ? values.filter(x => typeof x === 'string' && x.trim()) : [];
  if (!items.length) return <span className="text-sm text-color-secondary">Не указано</span>;
  const shown = limit ? items.slice(0, limit) : items;
  return <div className="flex flex-wrap gap-1 align-items-center">
    {shown.map((value, index) => <Tag key={`${index}_${value}`} value={value} severity="secondary" className="font-normal"/>)}
    {limit > 0 && items.length > limit && <span className="text-sm text-color-secondary" title={items.slice(limit).join(', ')}>+{items.length - limit}</span>}
  </div>;
};
const locationPoint = (loc) => {
  const rawLat = loc?.location?.lat, rawLng = loc?.location?.lng;
  if (rawLat === null || rawLat === undefined || rawLng === null || rawLng === undefined || String(rawLat).trim() === '' || String(rawLng).trim() === '') return null;
  const lat = Number(rawLat), lng = Number(rawLng);
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? {lat, lng} : null;
};
const branchMarkerIcon = (number, isMain) => L.divIcon({
  className: 'club-import-map-marker',
  html: `<span style="display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:50%;background:${isMain ? '#0f766e' : '#2563eb'};color:white;font-size:13px;font-weight:700;border:2px solid white;box-shadow:0 2px 8px #0005">${number}</span>`,
  iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -15]
});
const BranchMapController = ({points, focus}) => {
  const map = useMap();
  const pointKey = points.map(p => `${p.index}:${p.lat}:${p.lng}`).join('|');
  useEffect(() => {
    map.invalidateSize();
    if (points.length === 1) map.setView([points[0].lat, points[0].lng], 15, {animate: false});
    else if (points.length > 1) map.fitBounds(points.map(p => [p.lat, p.lng]), {padding: [35, 35], maxZoom: 15, animate: false});
  }, [map, pointKey]);
  useEffect(() => {
    if (!focus) return;
    const point = points.find(p => p.index === focus.index);
    if (point) map.setView([point.lat, point.lng], Math.max(map.getZoom(), 15));
  }, [map, focus]);
  useEffect(() => {
    const element = map.getContainer();
    let frame = null;
    const resize = () => {if (frame !== null) cancelAnimationFrame(frame); frame = requestAnimationFrame(() => map.invalidateSize());};
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    observer?.observe(element);
    resize();
    return () => {observer?.disconnect(); if (frame !== null) cancelAnimationFrame(frame);};
  }, [map]);
  return null;
};
const BranchMapPreview = ({locations, clubName}) => {
  const [focus, setFocus] = useState(null);
  const wrapperRef = useRef(null);
  const branches = Array.isArray(locations) ? locations : [];
  const points = branches.map((loc, index) => {
    const point = locationPoint(loc);
    return point ? {...point, index, loc} : null;
  }).filter(Boolean);
  const missingCount = branches.length - points.length;
  const showOnMap = (index) => {
    setFocus({index});
    wrapperRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
  };
  return <div className="surface-ground border-round p-3">
    <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
      <h4 className="m-0">Филиалы на карте</h4>
      <span className="text-sm text-color-secondary">С координатами: {points.length} из {branches.length}</span>
    </div>
    {points.length > 0 ? <div ref={wrapperRef} className="overflow-hidden border-round" style={{height: 340, width: '100%'}}>
      <MapContainer center={[points[0].lat, points[0].lng]} zoom={14} scrollWheelZoom={false} style={{height: '100%', width: '100%'}}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
        <BranchMapController points={points} focus={focus}/>
        {points.map(point => <Marker key={point.loc.source_id || point.loc.key || point.index} position={[point.lat, point.lng]} icon={branchMarkerIcon(point.index + 1, point.loc.is_main)}>
          <Popup><div>
            <strong>{point.loc.name || clubName || `Филиал ${point.index + 1}`}</strong>
            {point.loc.is_main && <div>Главный филиал</div>}
            <div>{point.loc.address || 'Адрес не указан'}</div>
            <small>{point.lat.toFixed(6)}, {point.lng.toFixed(6)}</small>
          </div></Popup>
        </Marker>)}
      </MapContainer>
    </div> : <div className="text-color-secondary text-sm p-3 border-1 surface-border border-round">Координаты пока не указаны. Филиалы появятся на карте после заполнения широты и долготы.</div>}
    {missingCount > 0 && <div className="mt-2 text-sm text-orange-600">Без координат: {missingCount}. Эти филиалы пока нельзя показать на карте.</div>}
    {branches.length > 0 && <div className="flex flex-column gap-2 mt-3">
      {branches.map((loc, index) => {
        const point = locationPoint(loc);
        return <div key={loc.source_id || loc.key || index} className="flex flex-wrap justify-content-between align-items-center gap-2 border-bottom-1 surface-border pb-2">
          <div className="flex-1" style={{minWidth: 170}}>
            <span className="font-semibold">{index + 1}. {loc.address || loc.name || 'Адрес не указан'}</span>
            {loc.is_main && <Tag className="ml-2" value="Главный" severity="success"/>}
          </div>
          {point ? <Btn label="На карте" icon="pi pi-map-marker" outlined onClick={() => showOnMap(index)}/> : <Tag value="Нет координат" severity="warning"/>}
        </div>;
      })}
    </div>}
    <small className="block mt-2 text-color-secondary">Карта использует сохранённые координаты. При изменении координат филиала маркер обновится автоматически.</small>
  </div>;
};
const cloneCandidate = (item) => ({
  ...item,
  data: {
    ...(item.data || {}),
    sports: [...(item.data?.sports || [])],
    locations: (item.data?.locations || []).map(loc => ({
      ...loc,
      location: {lat: valueText(loc.location?.lat), lng: valueText(loc.location?.lng)}
    }))
  }
});
const geoOptions = (items, lang) => (items || []).map(x => ({
  label: x.data?.names?.[lang] || x.name || x.id, value: x.id
}));

const CompanyImports = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('new');
  const [applied, setApplied] = useState({name: '', status: 'new'});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [duplicateCompanyId, setDuplicateCompanyId] = useState('');
  const [companyMatches, setCompanyMatches] = useState([]);
  const [checkingMatches, setCheckingMatches] = useState(false);
  const [matchesError, setMatchesError] = useState('');
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const requestId = useRef(0);
  const locationId = useRef(-1);
  const geoId = useRef(0);
  const matchId = useRef(0);
  const lang = appStore.user?.language || 'ru';
  const isAdmin = !!appStore.user?.is_admin;

  const load = async (currentPage = page, filters = applied) => {
    if (!isAdmin) return;
    const id = ++requestId.current;
    setLoading(true);
    try {
      const result = await post('adm/company_imports', {
        page: currentPage, page_size: PER_PAGE, name: filters.name, status: filters.status
      });
      if (id !== requestId.current) return;
      setRows(result.items || []);
      setCount(result.count || 0);
      if (currentPage > 1 && currentPage > (result.page_count || 0)) setPage(Math.max(1, result.page_count || 1));
    } catch (error) {
      if (id === requestId.current) systemStore.setError(message(error?.message, error?.message || 'Ошибка загрузки'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => { load(page, applied); }, [page, applied, isAdmin]);

  const applyFilters = (nextStatus = status) => {
    setPage(1);
    setApplied({name: search.trim(), status: nextStatus});
  };

  const loadGeo = async (countryId, regionId) => {
    const id = ++geoId.current;
    setRegions([]);
    setDistricts([]);
    if (!countryId) return;
    try {
      const res = await post('regions', {country_id: countryId, page_size: 200});
      if (id !== geoId.current) return;
      setRegions(res.items || []);
      if (!regionId) return;
      const dd = await post('districts', {region_id: regionId, page_size: 200});
      if (id === geoId.current) setDistricts(dd.items || []);
    } catch (error) {
      if (id === geoId.current) systemStore.setError(error?.message || 'Не удалось загрузить географию');
    }
  };

  const openEditor = async (item) => {
    const token = ++matchId.current;
    setSaving(true);
    setCompanyMatches([]);
    setMatchesError('');
    setCheckingMatches(true);
    try {
      const res = await post('adm/company_import/get', {id: item.id});
      const current = cloneCandidate(res.company_import);
      if (token !== matchId.current) return;
      setEditor(current);
      loadGeo(current.country_id, current.region_id);
      try {
        const matches = await findCompanyMatches(current);
        if (token === matchId.current) setCompanyMatches(matches);
      } catch (error) {
        if (token === matchId.current) setMatchesError('Не удалось проверить существующие клубы: ' + (error?.message || 'ошибка API'));
      }
    } catch (error) {
      systemStore.setError(error?.message || 'Не удалось открыть кандидата');
    } finally {
      if (token === matchId.current) {setSaving(false); setCheckingMatches(false);}
    }
  };

  const closeEditor = () => {geoId.current += 1; matchId.current += 1; setEditor(null); setCompanyMatches([]); setMatchesError(''); setCheckingMatches(false);};
  const change = (field, value) => setEditor(prev => ({...prev, [field]: value}));
  const changeData = (field, value) => setEditor(prev => ({...prev, data: {...prev.data, [field]: value}}));
  const changeLocation = (index, field, value) => setEditor(prev => ({
    ...prev, data: {...prev.data, locations: prev.data.locations.map((loc, i) => i === index ? {...loc, [field]: value} : loc)}
  }));
  const changePoint = (index, axis, value) => setEditor(prev => ({
    ...prev, data: {...prev.data, locations: prev.data.locations.map((loc, i) => i === index ? {
      ...loc, location: {...loc.location, [axis]: value}
    } : loc)}
  }));
  const setMain = (index) => setEditor(prev => ({
    ...prev, data: {...prev.data, locations: prev.data.locations.map((loc, i) => ({...loc, is_main: i === index}))}
  }));
  const addLocation = () => setEditor(prev => ({
    ...prev, data: {...prev.data, locations: [...prev.data.locations, {
      key: locationId.current--, source_id: null, name: '', address: '',
      location: {lat: '', lng: ''}, is_main: prev.data.locations.length === 0
    }]}
  }));
  const removeLocation = (index) => setEditor(prev => {
    const locations = prev.data.locations.filter((loc, i) => i !== index);
    if (locations.length && !locations.some(loc => loc.is_main)) locations[0] = {...locations[0], is_main: true};
    return {...prev, data: {...prev.data, locations}};
  });

  const saveEditor = async (approveAfterSave = false) => {
    if (!editor || saving) return;
    if (!editor.name?.trim()) return systemStore.setError('Укажите название клуба');
    if (approveAfterSave) {
      if (editor.status !== 'new' && editor.status !== 'review') return systemStore.setError('Этот кандидат недоступен для одобрения');
      if (!editor.country_id || !editor.region_id) return systemStore.setError('Укажите страну и регион');
      if (!editor.data?.sports?.length) return systemStore.setError('Выберите хотя бы один вид спорта');
      if (!editor.data?.locations?.length) return systemStore.setError('Добавьте хотя бы один филиал');
    }
    const locations = [];
    for (const [index, loc] of (editor.data.locations || []).entries()) {
      const latitude = valueText(loc.location?.lat).trim();
      const longitude = valueText(loc.location?.lng).trim();
      if ((latitude || longitude) && (!latitude || !longitude || !Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude)) || Math.abs(Number(latitude)) > 90 || Math.abs(Number(longitude)) > 180)) {
        return systemStore.setError(`Некорректные координаты филиала №${index + 1}`);
      }
      if (approveAfterSave && !valueText(loc.address).trim() && !(latitude && longitude)) {
        return systemStore.setError(`Для филиала №${index + 1} укажите адрес или координаты`);
      }
      locations.push({
        source_id: loc.source_id || null, name: loc.name || '', address: loc.address || '',
        location: latitude && longitude ? {lat: Number(latitude), lng: Number(longitude)} : null,
        is_main: !!loc.is_main
      });
    }
    const payload = {
      id: editor.id, name: editor.name, address: editor.address,
      country_id: editor.country_id, region_id: editor.region_id, district_id: editor.district_id,
      review_note: editor.review_note,
      data: {
        sports: editor.data.sports || [], locations,
        phone_number: editor.data.phone_number, website: editor.data.website,
        instagram: editor.data.instagram, whatsapp: editor.data.whatsapp,
        telegram: editor.data.telegram, about: editor.data.about,
        logo_source_url: editor.data.logo_source_url
      }
    };
    setSaving(true);
    let saved = false;
    try {
      if (approveAfterSave) {
        // Check the CURRENT edited name, not the stale name in the results table.
        const matches = await findCompanyMatches(editor);
        if (matches.length) {
          const top = matches[0];
          if (!window.confirm(`Возможно, клуб уже есть в Club #1:\n#${top.id} — ${top.name}\n\nОК — продолжить создание нового клуба.\nОтмена — проверить совпадение.`)) return;
        }
        if (!window.confirm(`Сохранить изменения и создать клуб «${editor.name}» с ${locations.length} филиал(ами)?\nПроверьте выбранные виды спорта и географию.`)) return;
      }
      await post('adm/company_import/save', payload);
      saved = true;
      if (approveAfterSave) {
        // Approval reads the just-saved candidate from the database.
        const res = await post('adm/company_import/approve', {id: editor.id});
        if (!res?.company_import?.company_id) throw new Error('API не подтвердил создание клуба');
        systemStore.setSuccess(`Изменения сохранены. Клуб #${res.company_import.company_id} принят`);
      } else {
        systemStore.setSuccess(message('successfully_saved', 'Сохранено'));
      }
      closeEditor();
      await load();
    } catch (error) {
      if (approveAfterSave && saved) {
        // Save and approve are two requests: preserve the editor when the second fails.
        setEditor(prev => prev?.id === editor.id ? {...prev, status: 'review'} : prev);
        systemStore.setError(`Изменения сохранены, но клуб не принят: ${error?.message || 'ошибка API'}`);
        await load();
      } else {
        systemStore.setError(error?.message || 'Ошибка сохранения');
      }
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (item, nextStatus, companyId = null) => {
    if (saving) return;
    const action = nextStatus === 'rejected' ? 'отклонить' : nextStatus === 'duplicate' ? 'отметить дубликатом' : 'вернуть на проверку';
    if (!window.confirm(`Вы хотите ${action} «${item.name}»?`)) return;
    setSaving(true);
    try {
      await post('adm/company_import/status', {
        id: item.id, status: nextStatus, company_id: companyId, review_note: item.review_note || null
      });
      setDuplicate(null);
      if (editor?.id === item.id) closeEditor();
      systemStore.setSuccess(message('successfully_saved', 'Сохранено'));
      await load();
    } catch (error) {
      systemStore.setError(message(error?.message, error?.message || 'Ошибка изменения статуса'));
    } finally { setSaving(false); }
  };

  const approve = async (item) => {
    if (saving) return;
    if (!item.data?.sports?.length) {
      systemStore.setError('Сначала подтвердите виды спорта у кандидата');
      await openEditor(item);
      return;
    }
    setSaving(true);
    try {
      const matches = await findCompanyMatches(item);
      if (matches.length) {
        const top = matches[0];
        const proceed = window.confirm(`Возможно, клуб уже существует в Club #1:\n#${top.id} — ${top.name}\n\nОК — продолжить создание НОВОГО клуба.\nОтмена — открыть карточку для проверки дубля.`);
        if (!proceed) {await openEditor(item); return;}
      }
      if (!window.confirm(`Создать клуб «${item.name}» и ${(item.data?.locations || []).length} филиал(ов)?\nУбедитесь, что виды спорта и география проверены.`)) return;
      const res = await post('adm/company_import/approve', {id: item.id});
      systemStore.setSuccess(`Клуб #${res.company_import?.company_id} создан`);
      await load();
    } catch (error) {
      systemStore.setError(message(error?.message, error?.message || 'Не удалось проверить дубли или подтвердить импорт'));
    } finally {setSaving(false);}
  };

  const doDuplicate = async () => {
    if (!duplicate) return;
    const raw = duplicateCompanyId.trim();
    if (raw && (!/^\d+$/.test(raw) || Number(raw) <= 0 || !Number.isSafeInteger(Number(raw)))) {
      return systemStore.setError('Некорректный ID существующего клуба');
    }
    await changeStatus(duplicate, 'duplicate', raw ? Number(raw) : null);
  };

  const statusTemplate = (item) => {
    const info = STATUS_VIEW[item.status] || {label: item.status, severity: 'secondary'};
    return <Tag value={info.label} severity={info.severity}/>;
  };
  const nameTemplate = (item) => <div>
    <strong>{item.name}</strong>
    <div className="text-sm text-color-secondary">#{item.id} · {item.source} · {item.source_id}</div>
    {/^https?:\/\//i.test(item.source_url || '') && <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="text-sm">Источник <i className="pi pi-external-link"/></a>}
  </div>;
  const locationTemplate = (item) => <div>
    <span>{item.address || 'Адрес не указан'}</span>
    <div className="text-sm text-color-secondary">Филиалов: {(item.data?.locations || []).length}</div>
  </div>;
  const sourceTemplate = (item) => <div className="flex flex-column gap-2">
    <div><div className="text-sm text-color-secondary mb-1">Поисковые запросы</div>{sourceTags(item.data?.searches, 3)}</div>
    <div><div className="text-sm text-color-secondary mb-1">Рубрики 2ГИС</div>{sourceTags(item.data?.source_categories, 3)}</div>
  </div>;
  const actionTemplate = (item) => <div className="flex flex-wrap gap-1">
    <Btn icon="pi pi-eye" label="Просмотр" outlined severity="secondary" disabled={saving} onClick={() => openEditor(item)}/>
    {item.status !== 'approved' && <>
      {(item.status === 'new' || item.status === 'review') && <>
        <Btn icon="pi pi-check" label="Принять" severity="success" disabled={saving} onClick={() => approve(item)}/>
        <Btn icon="pi pi-times" label="Отклонить" outlined severity="danger" disabled={saving} onClick={() => changeStatus(item, 'rejected')}/>
      </>}
      <Btn icon="pi pi-copy" label="Дубликат" outlined severity="secondary" disabled={saving} onClick={() => {setDuplicate(item); setDuplicateCompanyId('');}}/>
      {(item.status === 'rejected' || item.status === 'duplicate') && <Btn icon="pi pi-replay" label="Вернуть" outlined disabled={saving} onClick={() => changeStatus(item, 'review')}/>} 
    </>}
  </div>;

  const sportOptions = (appStore.sports || []).map(s => ({
    label: s.data?.names?.[lang] || s.name || s.id, value: s.id
  }));
  const sportLabel = (id) => sportOptions.find(x => x.value === id)?.label || id;
  const sportHints = editor ? suggestSports(editor).filter(x => sportOptions.some(s => s.value === x.id)) : [];
  const applySportHints = () => changeData('sports', [...new Set([...(editor.data.sports || []), ...sportHints.map(x => x.id)])]);
  const totalPages = Math.max(1, Math.ceil(count / PER_PAGE));

  if (!isAdmin) return null;
  return <PageLayout header={<div className="flex justify-content-between align-items-center w-full">
    <h3 className="m-0">Импорт клубов</h3>
    <Btn icon="pi pi-refresh" label="Обновить" outlined loading={loading} onClick={() => load()}/>
  </div>}>
    <div className="col-12">
      <div className="game-card mb-3">
        <div className="flex flex-wrap gap-2 align-items-end">
          <div className="flex-1" style={{minWidth: 180}}>
            <label className="block mb-1">Поиск по названию</label>
            <InputText className="w-full" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && applyFilters()}/>
          </div>
          <div style={{minWidth: 170}}>
            <label className="block mb-1">Статус</label>
            <Dropdown className="w-full" value={status} options={STATUSES} onChange={e => {setStatus(e.value); applyFilters(e.value);}}/>
          </div>
          <Btn icon="pi pi-search" label="Найти" onClick={() => applyFilters()} disabled={loading}/>
        </div>
      </div>
      <div className="flex justify-content-between align-items-center mb-2">
        <span className="text-color-secondary">Найдено: {count}</span>
        <span className="text-color-secondary">Страница {page} из {totalPages}</span>
      </div>
      <DataTable value={rows} loading={loading} dataKey="id" stripedRows scrollable emptyMessage="Кандидаты не найдены" size="small">
        <Column header="Клуб" body={nameTemplate} style={{minWidth: '210px'}}/>
        <Column header="Адреса" body={locationTemplate} style={{minWidth: '190px'}}/>
        <Column header="Данные поиска" body={sourceTemplate} style={{minWidth: '260px'}}/>
        <Column header="Статус" body={statusTemplate} style={{minWidth: '120px'}}/>
        <Column header="Действия" body={actionTemplate} style={{minWidth: '300px'}}/>
      </DataTable>
      <div className="flex justify-content-end gap-2 mt-3">
        <Btn icon="pi pi-angle-left" label="Назад" outlined disabled={loading || page <= 1} onClick={() => setPage(p => p - 1)}/>
        <Btn icon="pi pi-angle-right" label="Вперёд" outlined iconPos="right" disabled={loading || page >= totalPages} onClick={() => setPage(p => p + 1)}/>
      </div>
    </div>

    <Dialog header={editor ? `Кандидат #${editor.id}` : ''} visible={!!editor} onHide={() => {if (!saving) closeEditor();}} closable={!saving} modal maximizable style={{width: 'min(850px, 96vw)'}}>
      {editor && <div className="grid">
        <div className="col-12 md:col-8">
          <label className="block mb-1">Название клуба *</label>
          <InputText className="w-full" value={editor.name || ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => change('name', e.target.value)}/>
        </div>
        <div className="col-12 md:col-4">
          <label className="block mb-1">Статус</label>
          {statusTemplate(editor)}
          {editor.company_id && <div className="mt-2">Club # {editor.company_id}</div>}
        </div>
        <div className="col-12">
          <label className="block mb-1">Адрес</label>
          <InputText className="w-full" value={editor.address || ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => change('address', e.target.value)}/>
        </div>
        <div className="col-12 md:col-4">
          <label className="block mb-1">Страна *</label>
          <Dropdown className="w-full" filter options={geoOptions(appStore.countries, lang)} value={editor.country_id || null} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => {change('country_id', e.value); change('region_id', null); change('district_id', null); loadGeo(e.value, null);}}/>
        </div>
        <div className="col-12 md:col-4">
          <label className="block mb-1">Регион *</label>
          <Dropdown className="w-full" filter options={geoOptions(regions, lang)} value={editor.region_id || null} disabled={(editor.status === 'approved' || editor.status === 'duplicate') || !editor.country_id} onChange={e => {change('region_id', e.value); change('district_id', null); loadGeo(editor.country_id, e.value);}}/>
        </div>
        <div className="col-12 md:col-4">
          <label className="block mb-1">Район</label>
          <Dropdown className="w-full" filter showClear options={geoOptions(districts, lang)} value={editor.district_id || null} disabled={(editor.status === 'approved' || editor.status === 'duplicate') || !editor.region_id} onChange={e => change('district_id', e.value)}/>
        </div>
        <div className="col-12">
          <div className="surface-ground border-round p-3">
            <h4 className="mt-0 mb-2">Информация из поиска 2ГИС</h4>
            <div className="grid">
              <div className="col-12 md:col-6">
                <label className="block mb-2 font-semibold">Поисковые запросы</label>
                {sourceTags(editor.data.searches)}
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-2 font-semibold">Рубрики 2ГИС</label>
                {sourceTags(editor.data.source_categories)}
              </div>
            </div>
            <small className="text-color-secondary">Эти сведения помогают выбрать виды спорта, но не определяют их автоматически.</small>
          </div>
        </div>
        <div className="col-12">
          <div className="surface-ground border-round p-3">
            <div className="flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <h4 className="m-0">Подсказки видов спорта</h4>
              {sportHints.length > 0 && editor.status !== 'approved' && editor.status !== 'duplicate' && <Btn label="Добавить подсказки" outlined icon="pi pi-plus" onClick={applySportHints}/>}
            </div>
            {sportHints.length ? <div className="flex flex-wrap gap-2">
              {sportHints.map(hint => <Tag key={hint.id} severity={hint.source === 'name' ? 'info' : 'secondary'} value={`${sportLabel(hint.id)} · ${hint.source === 'name' ? 'название' : hint.source === 'rubrics' ? 'рубрика' : 'поисковая фраза'}`}/>)}
            </div> : <span className="text-sm text-color-secondary">Подсказок не найдено — выберите вид спорта вручную.</span>}
            <small className="block mt-2 text-color-secondary">Подсказки не означают, что клуб действительно проводит занятия по этому виду спорта. Они не сохраняются без выбора администратора.</small>
          </div>
        </div>
        <div className="col-12">
          <div className="surface-ground border-round p-3">
            <h4 className="mt-0 mb-2">Возможные дубли в Club #1</h4>
            {checkingMatches ? <span className="text-sm">Проверяем существующие клубы...</span> : matchesError ? <span className="text-sm text-red-500">{matchesError}</span> : companyMatches.length ? <div className="flex flex-column gap-2">
              {companyMatches.map(company => <div key={company.id} className="flex justify-content-between align-items-center gap-2 flex-wrap border-bottom-1 surface-border pb-2">
                <div><strong>#{company.id} · {company.name}</strong><div className="text-sm text-color-secondary">{company.region_name || editor.region_id} · {company.score >= 0.95 ? 'Совпадают ключевые слова названия' : 'Похожее название'}</div></div>
                {(editor.status !== 'approved' && editor.status !== 'duplicate') && <Btn label="Это дубликат" icon="pi pi-copy" outlined severity="warning" onClick={() => {setDuplicate(editor); setDuplicateCompanyId(String(company.id));}}/>}
              </div>)}
            </div> : <span className="text-sm text-color-secondary">Совпадений по названию не найдено. Это не исключает дубли с другим названием.</span>}
          </div>
        </div>
        <div className="col-12">
          <label className="block mb-1">Виды спорта *</label>
          <MultiSelect className="w-full" display="chip" filter options={sportOptions} value={editor.data.sports || []} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => changeData('sports', e.value)} placeholder="Выберите виды спорта"/>
        </div>
        {['phone_number', 'website', 'instagram', 'whatsapp', 'telegram'].map(field => <div className="col-12 md:col-6" key={field}>
          <label className="block mb-1">{field === 'phone_number' ? 'Телефон' : field.charAt(0).toUpperCase() + field.slice(1)}</label>
          <InputText className="w-full" value={editor.data[field] || ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => changeData(field, e.target.value)}/>
        </div>)}
        <div className="col-12">
          <label className="block mb-1">Описание</label>
          <InputTextarea rows={3} autoResize className="w-full" value={editor.data.about || ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => changeData('about', e.target.value)}/>
        </div>
        <div className="col-12">
          <label className="block mb-1">Ссылка на логотип (источник)</label>
          <InputText className="w-full" value={editor.data.logo_source_url || ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => changeData('logo_source_url', e.target.value)}/>
        </div>
        <div className="col-12">
          <BranchMapPreview key={editor.id} locations={editor.data.locations || []} clubName={editor.name}/>
        </div>
        <div className="col-12 flex align-items-center justify-content-between">
          <h4 className="m-0">Филиалы ({editor.data.locations.length})</h4>
          {(editor.status !== 'approved' && editor.status !== 'duplicate') && <Btn icon="pi pi-plus" label="Добавить" outlined onClick={addLocation}/>}
        </div>
        {(editor.data.locations || []).map((loc, index) => <div className="col-12" key={loc.source_id || loc.key || index}>
          <div className="surface-ground border-round p-3">
            <div className="flex justify-content-between align-items-center mb-2">
              <div className="flex align-items-center gap-2">
                <RadioButton inputId={`main_${index}`} name="main_location" checked={!!loc.is_main} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={() => setMain(index)}/>
                <label htmlFor={`main_${index}`} className="font-semibold">Филиал {index + 1} {loc.is_main ? '· Главный' : ''}</label>
              </div>
              {(editor.status !== 'approved' && editor.status !== 'duplicate') && <Btn icon="pi pi-trash" severity="danger" outlined tooltip="Удалить филиал" onClick={() => removeLocation(index)}/>}
            </div>
            {loc.source_id && <div className="text-sm text-color-secondary mb-2">ID источника: {loc.source_id}</div>}
            <div className="grid">
              <div className="col-12 md:col-6"><label className="block mb-1">Название</label><InputText className="w-full" value={loc.name || ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => changeLocation(index, 'name', e.target.value)}/></div>
              <div className="col-12 md:col-6"><label className="block mb-1">Адрес</label><InputText className="w-full" value={loc.address || ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => changeLocation(index, 'address', e.target.value)}/></div>
              <div className="col-6"><label className="block mb-1">Широта (lat)</label><InputText className="w-full" value={loc.location?.lat ?? ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => changePoint(index, 'lat', e.target.value)}/></div>
              <div className="col-6"><label className="block mb-1">Долгота (lng)</label><InputText className="w-full" value={loc.location?.lng ?? ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => changePoint(index, 'lng', e.target.value)}/></div>
            </div>
          </div>
        </div>)}
        <div className="col-12"><label className="block mb-1">Примечание администратора</label><InputTextarea rows={2} autoResize className="w-full" value={editor.review_note || ''} disabled={(editor.status === 'approved' || editor.status === 'duplicate')} onChange={e => change('review_note', e.target.value)}/></div>
        <div className="col-12 flex justify-content-end gap-2">
          <Btn label="Закрыть" outlined disabled={saving} onClick={closeEditor}/>
          {(editor.status !== 'approved' && editor.status !== 'duplicate') && <Btn icon="pi pi-save" label="Сохранить" outlined loading={saving} disabled={saving} onClick={() => saveEditor(false)}/>}
          {(editor.status === 'new' || editor.status === 'review') && <Btn icon="pi pi-check" label="Сохранить и принять" severity="success" loading={saving} disabled={saving} onClick={() => saveEditor(true)}/>}
        </div>
      </div>}
    </Dialog>

    <Dialog header="Отметить дубликатом" visible={!!duplicate} onHide={() => setDuplicate(null)} modal style={{width: 'min(450px, 95vw)'}}>
      {duplicate && <div className="flex flex-column gap-3">
        <div><strong>{duplicate.name}</strong></div>
        <div>Введите ID существующего клуба, если он известен. Поле можно оставить пустым.</div>
        <InputText className="w-full" placeholder="Company ID" value={duplicateCompanyId} onChange={e => setDuplicateCompanyId(e.target.value)}/>
        <div className="flex justify-content-end gap-2">
          <Btn label="Отмена" outlined onClick={() => setDuplicate(null)}/>
          <Btn label="Подтвердить" severity="secondary" disabled={saving} loading={saving} onClick={doDuplicate}/>
        </div>
      </div>}
    </Dialog>
  </PageLayout>;
};

export default observer(CompanyImports);
